import { categoryMatches } from './categoryTree.ts';
import type { DeckSummary } from '../../shared/deck.ts';

/**
 * The deck list's filter engine.
 *
 * Pure on purpose: this repo has no way to render a component in a test (bun
 * test, no jsdom, no testing-library), so every decision the dashboard makes
 * about WHICH decks to show is made here, where it can be asserted. The
 * components above it choose how things look and nothing else. Same argument as
 * `categoryTree.ts` and `coverWarm.ts`.
 *
 * Two rules that keep it testable and must not be broken:
 *
 *   1. `now` is a PARAMETER. Nothing here calls `Date.now()`, or the "updated
 *      in the last 7 days" boundaries could only be tested by moving the clock.
 *   2. `facetCounts` computes a facet's own counts with that facet's selection
 *      REMOVED. That is the standard faceted-search rule, and without it
 *      ticking one owner shows every other owner as "0", which reads as "there
 *      are none" rather than "you have not picked them".
 */

/** The Uncategorised bucket, as a value the `categories` facet can hold. A
 *  leading NUL-ish space cannot collide with a real path, which is trimmed and
 *  non-empty by `cleanCategory`. */
export const UNCATEGORISED = ' uncategorised';
/** Decks nobody is down as owning. */
export const UNASSIGNED_OWNER = ' unassigned';
/** Decks with no build provenance — hand-made, imported, or built before the
 *  stamp existed. Absence is not "we checked and it was nothing". */
export const UNKNOWN_MODEL = ' unknown';

export type DeckStatus = 'published' | 'unpublished' | 'trash';

/** A relative window over `lastEditedAt`, or an explicit pair of dates. */
export type UpdatedRange =
  | { kind: 'any' }
  | { kind: 'today' | '7d' | '30d' | '3m' | 'year' }
  /** `from`/`to` are YYYY-MM-DD, inclusive at both ends, either may be null. */
  | { kind: 'custom'; from: string | null; to: string | null };

export type DeckFilter = {
  /** Free text over title, slug, category and tag. */
  q: string;
  status: DeckStatus | null;
  /** OR within the list, AND against every other facet. A parent path also
   *  selects its children. May hold UNCATEGORISED. */
  categories: string[];
  tags: string[];
  /** Person ids, or UNASSIGNED_OWNER. */
  owners: string[];
  /** `engine·model` keys, or UNKNOWN_MODEL. See `modelKeyFor`. */
  models: string[];
  updated: UpdatedRange;
  /** Inclusive slide-count bounds; null means unbounded on that side. */
  slides: { min: number | null; max: number | null };
  starred: boolean;
  missingCover: boolean;
  /** Decks that have been opened at least once. The rail's "Recently opened",
   *  which is a set and not just an ordering — its count says so. */
  opened: boolean;
  neverOpened: boolean;
};

/** Every facet, as a key. A literal union rather than `string` so
 *  `facetCounts` cannot be handed a name that does not exist — and so nothing
 *  here has to reach for `any`, which lint treats as an error. */
export type FacetKey = keyof DeckFilter;

export function emptyFilter(): DeckFilter {
  return {
    q: '',
    status: null,
    categories: [],
    tags: [],
    owners: [],
    models: [],
    updated: { kind: 'any' },
    slides: { min: null, max: null },
    starred: false,
    missingCover: false,
    opened: false,
    neverOpened: false,
  };
}

/**
 * Whether a deck belongs on the dashboard at all.
 *
 * Two exclusions that used to live in two different places: the server already
 * drops `library-item` decks from `GET /api/decks`, and the dashboard dropped
 * `bank` decks itself while rendering. Both are applied HERE, before anything
 * is counted, so the header's "N decks" and the grid can never disagree.
 */
export function isBrowsable(deck: DeckSummary): boolean {
  return deck.kind !== 'bank' && deck.kind !== 'library-item';
}

/** Published means it has a public share URL — the same test the card's badge
 *  makes, so the filter and the badge can never contradict each other. */
export function isPublished(deck: DeckSummary): boolean {
  return !!deck.publishedUrl;
}

/**
 * The stable key for a deck's build provenance.
 *
 * `engine·model` lowercased, so two decks built on the same model through
 * different engines stay distinguishable — that comparison is most of why the
 * provenance is recorded. The label is a display concern and lives in
 * `format.ts`; this is only the identity.
 */
export function modelKeyFor(deck: DeckSummary): string {
  const built = deck.builtBy;
  if (!built || !built.engine || !built.model) return UNKNOWN_MODEL;
  return `${built.engine}·${built.model}`.toLowerCase();
}

/**
 * A deck that could show a cover but has none.
 *
 * Broken and empty decks are excluded rather than counted: they have nothing to
 * render, so listing them under "Missing a thumbnail" would be a to-do list of
 * work nobody can do. Note `cover` is only present at all on a `?covers=1`
 * read, and the listing reports only ALREADY-CACHED covers, so this reads as
 * "not cached yet" on a cold studio.
 */
export function isMissingCover(deck: DeckSummary): boolean {
  if (deck.broken || deck.slideCount === 0) return false;
  return !deck.cover;
}

/** Start of the day `ms` falls in, local time — the dashboard's dates are read
 *  in the reader's own timezone, not UTC. */
function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Inclusive lower bound in ms for a relative window, or null for "any time". */
function updatedFloor(range: UpdatedRange, now: number): number | null {
  switch (range.kind) {
    case 'any':
      return null;
    // "Today" means since midnight, not "within 24 hours" — a deck edited last
    // night is yesterday's, however few hours ago that was.
    case 'today':
      return startOfDay(now);
    case '7d':
      return startOfDay(now) - 6 * 86400000;
    case '30d':
      return startOfDay(now) - 29 * 86400000;
    case '3m':
      return startOfDay(now) - 89 * 86400000;
    case 'year': {
      const d = new Date(now);
      return new Date(d.getFullYear(), 0, 1).getTime();
    }
    case 'custom':
      return range.from ? Date.parse(range.from + 'T00:00:00') : null;
  }
}

/** Inclusive upper bound in ms, or null. Only a custom range has one. */
function updatedCeiling(range: UpdatedRange): number | null {
  if (range.kind !== 'custom' || !range.to) return null;
  // Inclusive of the whole `to` day, so picking the same date twice matches it.
  return Date.parse(range.to + 'T23:59:59.999');
}

function matchesUpdated(deck: DeckSummary, range: UpdatedRange, now: number): boolean {
  const floor = updatedFloor(range, now);
  const ceiling = updatedCeiling(range);
  if (floor !== null && deck.lastEditedAt < floor) return false;
  if (ceiling !== null && deck.lastEditedAt > ceiling) return false;
  return true;
}

function matchesQuery(deck: DeckSummary, q: string): boolean {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return (
    deck.title.toLowerCase().includes(needle) ||
    deck.slug.toLowerCase().includes(needle) ||
    (deck.category ? deck.category.toLowerCase().includes(needle) : false) ||
    (deck.tags || []).some((t) => t.toLowerCase().includes(needle))
  );
}

/** The FILTER stays plural even though a deck's own category is singular:
 *  ticking three categories is an OR across them, which is a question about the
 *  query, not about how many homes a deck has. */
function matchesCategories(deck: DeckSummary, selected: string[]): boolean {
  if (!selected.length) return true;
  const cat = deck.category;
  return selected.some((value) => (value === UNCATEGORISED ? !cat : !!cat && categoryMatches(cat, value)));
}

function matchesTags(deck: DeckSummary, selected: string[]): boolean {
  if (!selected.length) return true;
  const tags = (deck.tags || []).map((t) => t.toLowerCase());
  return selected.some((value) => tags.includes(value.toLowerCase()));
}

function matchesOwners(deck: DeckSummary, selected: string[]): boolean {
  if (!selected.length) return true;
  return selected.some((value) => (value === UNASSIGNED_OWNER ? !deck.ownerId : deck.ownerId === value));
}

function matchesModels(deck: DeckSummary, selected: string[]): boolean {
  if (!selected.length) return true;
  const key = modelKeyFor(deck);
  return selected.some((value) => value.toLowerCase() === key);
}

function matchesSlides(deck: DeckSummary, bounds: { min: number | null; max: number | null }): boolean {
  if (bounds.min !== null && deck.slideCount < bounds.min) return false;
  if (bounds.max !== null && deck.slideCount > bounds.max) return false;
  return true;
}

function matchesStatus(deck: DeckSummary, status: DeckStatus | null): boolean {
  // Trash is its own surface, not a narrowing of the grid: no live deck passes.
  if (status === 'trash') return false;
  if (status === 'published') return isPublished(deck);
  if (status === 'unpublished') return !isPublished(deck);
  return true;
}

/** Whether one facet accepts one deck. Split out so `filterDecks` and
 *  `facetCounts` can never drift apart on what a facet means. */
function matchesFacet(deck: DeckSummary, facet: FacetKey, filter: DeckFilter, now: number): boolean {
  switch (facet) {
    case 'q':
      return matchesQuery(deck, filter.q);
    case 'status':
      return matchesStatus(deck, filter.status);
    case 'categories':
      return matchesCategories(deck, filter.categories);
    case 'tags':
      return matchesTags(deck, filter.tags);
    case 'owners':
      return matchesOwners(deck, filter.owners);
    case 'models':
      return matchesModels(deck, filter.models);
    case 'updated':
      return matchesUpdated(deck, filter.updated, now);
    case 'slides':
      return matchesSlides(deck, filter.slides);
    case 'starred':
      return !filter.starred || deck.starred;
    case 'missingCover':
      return !filter.missingCover || isMissingCover(deck);
    case 'opened':
      return !filter.opened || deck.lastOpenedAt !== null;
    case 'neverOpened':
      // Never opened is `null`, not `0`: an epoch-zero stamp would be a deck
      // opened in 1970, which is a different (and impossible) claim.
      return !filter.neverOpened || deck.lastOpenedAt === null;
  }
}

/** Every facet key, in a fixed order — the order the chips and the empty
 *  state's explanation read in. */
export function facetKeys(): FacetKey[] {
  return [
    'q',
    'status',
    'categories',
    'tags',
    'owners',
    'models',
    'updated',
    'slides',
    'starred',
    'missingCover',
    'opened',
    'neverOpened',
  ];
}

/** Whether one facet is doing anything. */
export function facetActive(filter: DeckFilter, facet: FacetKey): boolean {
  switch (facet) {
    case 'q':
      return filter.q.trim() !== '';
    case 'status':
      return filter.status !== null;
    case 'categories':
      return filter.categories.length > 0;
    case 'tags':
      return filter.tags.length > 0;
    case 'owners':
      return filter.owners.length > 0;
    case 'models':
      return filter.models.length > 0;
    case 'updated':
      return filter.updated.kind !== 'any';
    case 'slides':
      return filter.slides.min !== null || filter.slides.max !== null;
    case 'starred':
      return filter.starred;
    case 'missingCover':
      return filter.missingCover;
    case 'opened':
      return filter.opened;
    case 'neverOpened':
      return filter.neverOpened;
  }
}

/** Is anything narrowing the list? Drives "Showing N of M" and the Clear all. */
export function isFiltering(filter: DeckFilter): boolean {
  return facetKeys().some((facet) => facetActive(filter, facet));
}

/** How many facets are active — the "N active" badge on the More filters sheet. */
export function activeFacetCount(filter: DeckFilter): number {
  return facetKeys().filter((facet) => facetActive(filter, facet)).length;
}

/** The decks the current filter shows. Browsable decks only — bank and library
 *  items never reach the grid or the counts. */
export function filterDecks(decks: DeckSummary[], filter: DeckFilter, now: number): DeckSummary[] {
  return decks
    .filter(isBrowsable)
    .filter((deck) => facetKeys().every((facet) => matchesFacet(deck, facet, filter, now)));
}

/** The decks that pass every facet EXCEPT `except` — the population a facet's
 *  own option counts are measured against. */
export function decksExcluding(decks: DeckSummary[], filter: DeckFilter, except: FacetKey, now: number): DeckSummary[] {
  return decks
    .filter(isBrowsable)
    .filter((deck) => facetKeys().every((facet) => facet === except || matchesFacet(deck, facet, filter, now)));
}

/**
 * How many decks each option of one facet would show.
 *
 * Measured against everything EXCEPT this facet's own selection, which is the
 * rule that makes a multi-select usable: with "Jake" ticked, Sophie's row still
 * reads its real count rather than 0, so it reads as "4 more if you add her"
 * instead of "there are none".
 *
 * Returns a plain map from option value to count. Options with no decks are
 * absent rather than 0, so a caller can tell "none" from "not applicable" —
 * a registry entry that exists but has nothing on it still needs a row.
 */
export function facetCounts(
  decks: DeckSummary[],
  filter: DeckFilter,
  facet: 'categories' | 'tags' | 'owners' | 'models',
  now: number,
): Record<string, number> {
  const population = decksExcluding(decks, filter, facet, now);
  const counts: Record<string, number> = {};
  const bump = (key: string) => {
    counts[key] = (counts[key] ?? 0) + 1;
  };

  for (const deck of population) {
    if (facet === 'categories') {
      const cat = deck.category;
      if (!cat) bump(UNCATEGORISED);
      // A deck under `a/b` counts toward `a` as well as toward `a/b`.
      else {
        bump(cat);
        if (cat.includes('/')) bump(cat.slice(0, cat.indexOf('/')));
      }
    } else if (facet === 'tags') {
      const seen = new Set<string>();
      for (const tag of deck.tags || []) {
        const key = tag.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        bump(tag);
      }
    } else if (facet === 'owners') {
      bump(deck.ownerId || UNASSIGNED_OWNER);
    } else {
      bump(modelKeyFor(deck));
    }
  }
  return counts;
}

/** The smallest and largest slide counts across the browsable decks — the ends
 *  of the deck-size slider. Null when there are no decks to measure. */
export function slideCountBounds(decks: DeckSummary[]): { min: number; max: number } | null {
  const counts = decks.filter(isBrowsable).map((d) => d.slideCount);
  if (!counts.length) return null;
  return { min: Math.min(...counts), max: Math.max(...counts) };
}

/**
 * Which single facet, if dropped, would bring results back — the empty state's
 * "Owner: Marcin and Tag: Content Team have no decks in common".
 *
 * Only reports when exactly ONE removal helps, because that is the only case
 * where the page can offer a single honest button. When several would work, or
 * none would, the answer is "clear all filters" and this returns [].
 */
export function conflictingFacets(decks: DeckSummary[], filter: DeckFilter, now: number): FacetKey[] {
  if (filterDecks(decks, filter, now).length > 0) return [];
  const active = facetKeys().filter((facet) => facetActive(filter, facet));
  const rescuers = active.filter((facet) => decksExcluding(decks, filter, facet, now).length > 0);
  return rescuers.length === 1 ? rescuers : [];
}
